// MakerDAO-style fixed-point math, done in BigInt to avoid the precision loss
// of converting 27- and 45-decimal on-chain values through JS `Number`.
//
// Units:  WAD = 1e18 (token amounts, ratios) · RAY = 1e27 (rates, prices) · RAD = 1e45 (internal DAI)
// All exported math takes/returns BigInt in WAD unless noted; convert to a JS
// number for display only at the very end via `wadToNumber`.

export const WAD = 10n ** 18n;
export const RAY = 10n ** 27n;
export const RAD = 10n ** 45n;

// WAD-precision multiply / divide.
export const wmul = (a, b) => (a * b) / WAD;
export const wdiv = (a, b) => (a * WAD) / b;

/**
 * Normalized debt (`dart`) required to draw `wad` JDAI given the ilk `rate` (RAY).
 * frob credits internal DAI = rate * dart; an exit of `wad` needs `wad * RAY`, so
 * dart = ceil(wad * RAY / rate) — the full amount is withdrawable with no dust.
 */
export const mintDart = (wad, rate) => {
  if (rate <= 0n) return wad; // fallback: rate should never be 0
  return (wad * RAY + rate - 1n) / rate;
};

/**
 * Oracle PLS price in USD as a WAD, from Vat `spot` and Spotter `mat`/`par` (all RAY).
 * price = spot * mat * par / RAY^3  (then scaled to WAD).
 */
export const oraclePlsPriceWad = (spot, mat, par) => {
  if (spot <= 0n || mat <= 0n || par <= 0n) return 0n;
  return (spot * mat * par * WAD) / (RAY * RAY * RAY);
};

/** JDAI oracle target (USD, WAD) from `par` (RAY). par is the gold-pegged target. */
export const jdaiTargetWad = (par) => (par * WAD) / RAY;

/** Liquidation ratio (`mat`, RAY) expressed as a WAD multiplier (e.g. 1.5e18). */
export const matToWad = (mat) => (mat * WAD) / RAY;

/**
 * Collateralization / health ratio as a WAD (1e18 == 100%).
 *   health = (collateral * plsPrice) / (debt * jdaiTarget)
 * Returns null when there is no debt (caller renders ∞).
 */
export const healthRatioWad = (collateralWad, plsPriceWad, debtWad, targetWad) => {
  if (debtWad <= 0n || targetWad <= 0n) return null;
  const debtValue = wmul(debtWad, targetWad);
  if (debtValue <= 0n) return null;
  return wdiv(wmul(collateralWad, plsPriceWad), debtValue);
};

/**
 * Max JDAI debt (WAD) mintable against collateral at the liquidation ratio.
 *   maxDebt = (collateralValue / matRatio) / jdaiTarget
 */
export const maxDebtWad = (collateralWad, plsPriceWad, mat, targetWad) => {
  if (mat <= 0n || targetWad <= 0n) return 0n;
  const collateralValue = wmul(collateralWad, plsPriceWad); // WAD USD
  const atMat = (collateralValue * RAY) / mat;              // divide by mat ratio
  return wdiv(atMat, targetWad);                            // -> WAD JDAI
};

/**
 * PLS price (WAD USD) at which an active vault reaches the liquidation ratio.
 *   liqPrice = matRatio * debtValue / collateral
 * (Below this price the vault becomes liquidatable.)
 */
export const liquidationPriceWad = (collateralWad, debtWad, targetWad, mat) => {
  if (collateralWad <= 0n || debtWad <= 0n || mat <= 0n) return 0n;
  const debtValue = wmul(debtWad, targetWad);      // WAD USD
  const withMat = (debtValue * mat) / RAY;         // * matRatio
  return wdiv(withMat, collateralWad);             // -> WAD USD price
};

/**
 * PLS (WAD) required to collateralize the debt floor (`dust`, RAD) at current `spot` (RAY).
 *   plsRequired = dust / spot   (result already in WAD)
 */
export const plsRequiredForDebtFloor = (dustRad, spot) => (spot > 0n ? dustRad / spot : 0n);

/**
 * True vault debt in JDAI (WAD) from normalized debt `art` (WAD) and ilk `rate` (RAY).
 * tab = art * rate, rounded UP so repaying this amount always covers the full debt.
 */
export const debtWadFromArt = (art, rate) => {
  if (art <= 0n) return 0n;
  if (rate <= 0n) return art;
  return (art * rate + RAY - 1n) / RAY;
};

// Requests within this of the full tab count as "repay everything" — amounts round-trip
// through display strings and JS floats, so an exact-wei match can't be expected. 1e-6
// JDAI is far below the 1-JDAI dust floor, so treating it as full intent is always safe.
export const FULL_REPAY_TOL = 10n ** 12n;

/**
 * Normalized-debt reduction (positive magnitude; negate for frob's `dart`) to repay
 * `reqWad` JDAI against a vault with `art` (WAD), `rate` (RAY), and internal balance
 * `daiRad` (RAD).
 *
 * Full repay (request within FULL_REPAY_TOL of the tab, and the internal balance covers
 * it) returns `art` exactly — art ends at 0 and the dust check can't trip. Anything else
 * is a partial: floor(req / rate), capped by the internal balance and by `art`.
 */
export const repayDart = (reqWad, art, rate, daiRad) => {
  if (reqWad <= 0n || art <= 0n || rate <= 0n) return 0n;
  const tab = art * rate; // RAD
  if ((reqWad + FULL_REPAY_TOL) * RAY >= tab && daiRad >= tab) return art;
  let d = (reqWad * RAY) / rate;
  const byBalance = daiRad / rate;
  if (d > byBalance) d = byBalance;
  if (d > art) d = art;
  return d;
};

/**
 * JDAI (WAD) that DaiJoin.join must deposit so the repay frob can settle. A full-intent
 * repay needs the whole tab (art * rate) in vat.dai — joining just the face amount is a
 * hair short once rate > 1, stranding the vault in the dust band. Credits whatever is
 * already in `daiRad`; returns 0 when no join is needed.
 */
export const repayJoinWad = (reqWad, art, rate, daiRad) => {
  if (reqWad <= 0n || art <= 0n || rate <= 0n) return 0n;
  const tab = art * rate;
  const neededRad = (reqWad + FULL_REPAY_TOL) * RAY >= tab ? tab : reqWad * RAY;
  if (daiRad >= neededRad) return 0n;
  return (neededRad - daiRad + RAY - 1n) / RAY; // ceil to WAD
};

/**
 * Plan a borrow: PLS (WAD) that must be locked so that existing debt plus `wantWad`
 * new JDAI sits at `targetRatioWad` collateralization (WAD; e.g. 2e18 = 200%).
 * `spot` (RAY) already embeds mat and par — ink at exactly the enforced minimum is
 * debt·RAY/spot — so the target is applied as targetRatio/mat on top of that line.
 * A 0.1% pad absorbs float round-trips across the join/frob legs. Returns null when
 * the inputs can't produce a plan (including a target below the enforced minimum).
 */
export const borrowPlan = (wantWad, targetRatioWad, matWad, { ink, art, rate, spot }) => {
  if (wantWad <= 0n || spot <= 0n || matWad <= 0n || targetRatioWad < matWad) return null;
  const newDebtWad = debtWadFromArt(art, rate) + wantWad;
  const inkAtMat = (newDebtWad * RAY + spot - 1n) / spot;            // WAD, at the Vat line
  let requiredInkWad = (inkAtMat * targetRatioWad + matWad - 1n) / matWad;
  requiredInkWad = (requiredInkWad * 1001n) / 1000n;
  const additionalInkWad = requiredInkWad > ink ? requiredInkWad - ink : 0n;
  return { newDebtWad, requiredInkWad, additionalInkWad };
};

/**
 * Plan the largest debt reduction payable purely from an internal vat.dai balance
 * without tripping the dust floor — used by the stranded-funds banner, where the
 * user has internal DAI but may not hold enough to clear the whole tab.
 *
 * Returns { dart, fullClear }:
 *  - fullClear: internal balance covers the entire tab -> dart == art (vault closes).
 *  - otherwise dart = floor(min(tab - dust, daiRad) / rate): remaining debt stays at
 *    or above the floor, and the spend never exceeds the balance. dart 0n = only a
 *    withdrawal makes sense.
 */
export const strandedRepayPlan = (art, rate, daiRad, dustRad) => {
  if (art <= 0n || rate <= 0n || daiRad <= 0n) return { dart: 0n, fullClear: false };
  const tab = art * rate; // RAD
  if (daiRad >= tab) return { dart: art, fullClear: true };
  const maxByDust = tab > dustRad ? (tab - dustRad) / rate : 0n;
  const maxByBalance = daiRad / rate;
  const dart = maxByDust < maxByBalance ? maxByDust : maxByBalance;
  return { dart: dart > 0n ? dart : 0n, fullClear: false };
};

/**
 * Would reducing `art` by `d` land the urn in the forbidden band — nonzero debt below
 * the `dust` floor (RAD)? Mirrors the Vat's own check so we can fail with a readable
 * message instead of an on-chain "Vat/dust" revert.
 */
export const repayLeavesDust = (d, art, rate, dustRad) => {
  const left = art - d;
  if (left <= 0n) return false;
  return left * rate < dustRad;
};

/** Convert a WAD BigInt to a JS number for display (magnitudes here are display-safe). */
export const wadToNumber = (wad) => {
  const neg = wad < 0n;
  const s = (neg ? -wad : wad).toString().padStart(19, '0');
  const intPart = s.slice(0, -18);
  const frac = s.slice(-18);
  return parseFloat((neg ? '-' : '') + intPart + '.' + frac);
};

/** Convert a small positive JS number to a WAD BigInt (used for market-price fallback). */
export const numberToWad = (n) => {
  if (!n || !isFinite(n) || n <= 0) return 0n;
  const [intPart, fracPartRaw = ''] = n.toFixed(18).split('.');
  const frac = (fracPartRaw + '0'.repeat(18)).slice(0, 18);
  return BigInt(intPart) * WAD + BigInt(frac);
};

/**
 * Debt-floor ("dust") guard. A vault's debt must be either 0 or >= the floor — never in
 * between. Returns a human-readable reason if the requested mint/repay `amount` (JDAI)
 * would leave the vault in that forbidden band, otherwise null.
 *
 * @param {string} operation           'mint-jdai' | 'repay-jdai'
 * @param {number|string} amount       requested amount (JDAI)
 * @param {number|string} currentDebt  current vault debt (JDAI)
 * @param {number|string} floor        debt floor / dust (JDAI)
 * @returns {string|null}
 */
export const debtFloorViolation = (operation, amount, currentDebt, floor) => {
  const amt = Number(amount);
  const debt = Number(currentDebt) || 0;
  const min = Number(floor) || 0;
  if (!(amt > 0) || min <= 0) return null;
  const EPS = 1e-9;
  const fmt = (x) => String(+Number(x).toFixed(6));

  if (operation === 'mint-jdai') {
    const newDebt = debt + amt;
    if (newDebt > EPS && newDebt < min - EPS) {
      return `Minimum vault debt is ${fmt(min)} JDAI. Mint at least ${fmt(min - debt)} more JDAI (to reach ${fmt(min)}).`;
    }
  } else if (operation === 'repay-jdai') {
    const remaining = debt - amt;
    if (remaining > EPS && remaining < min - EPS) {
      return `That would leave ${fmt(remaining)} JDAI of debt, below the ${fmt(min)} JDAI minimum. Repay your full ${fmt(debt)} JDAI, or leave at least ${fmt(min)}.`;
    }
  }
  return null;
};
